import {
  type Replicants,
  type TrialPlot,
  type NewTrialStateInterface,
  type allCombineType,
  type BufferZone,
  type MachineryGrid,
  type AbLine,
} from "../types";
import { splitStripGroupsIntoPlots } from "./splitStripPlots";
import { cleanAbLines, mergeStripsDataIntoPayload } from "../utils";

export const removeKeysFromObject = (
  obj: allCombineType,
  keysToRemove: string[],
  excludeKeys: string[] = []
): any =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([k]) => !keysToRemove.includes(k) && !excludeKeys.includes(k)
    )
  );

const CLIENT_ONLY_KEYS = ["id", "visible", "key"];

const modifyTrialPlots = (item: TrialPlot): any => ({
  ...item,
  plot: removeKeysFromObject(item.plot, CLIENT_ONLY_KEYS),
  replicants: item.replicants?.map((replicant: Replicants) =>
    removeKeysFromObject(replicant, [...CLIENT_ONLY_KEYS, "parentId"])
  ),
});

const cleanAbLineEntries = (abLine: AbLine[] = []): AbLine[] =>
  abLine
    .map((entry) => removeKeysFromObject(entry, CLIENT_ONLY_KEYS))
    .map((entry) => removeKeysFromObject(entry, [], ["ticked"]));

const cleanNamedEntries = <T extends BufferZone | MachineryGrid>(
  entries: T[] = []
): T[] =>
  entries
    .map((entry) => removeKeysFromObject(entry, CLIENT_ONLY_KEYS))
    .map((entry) => removeKeysFromObject(entry, [], ["name"]));

const cleanTrialPlotEntries = (trialPlots: TrialPlot[] = []): any[] =>
  trialPlots
    .map((entry) => removeKeysFromObject(entry, CLIENT_ONLY_KEYS))
    .map(modifyTrialPlots);

/**
 * Strips client-only bookkeeping keys (id/visible/key/parentId/ticked/name)
 * out of every Plot Design entity before it goes on the wire. Pulled out of
 * `useFormHandling` so both the wizard's Save/Next flow and a standalone
 * persist-on-delete flow can build the identical payload shape.
 */
export const removeKeys = (
  newTrialState: NewTrialStateInterface
): NewTrialStateInterface => {
  const modifiedData: NewTrialStateInterface = {
    ...newTrialState,
    ab_line: cleanAbLineEntries(newTrialState.ab_line),
    buffer_zone: cleanNamedEntries(newTrialState.buffer_zone),
    machinery_grid: cleanNamedEntries(newTrialState.machinery_grid),
    trial_plots: cleanTrialPlotEntries(newTrialState.trial_plots),
  };

  modifiedData.machinery_grid.forEach((mgItem) => {
    if (mgItem) mgItem.rotation = 0;
  });

  return modifiedData;
};

export function processTrialPlots(trialPlots: any[]): any[] {
  return trialPlots.map((entry) => {
    const { plot, properties } = entry;
    const totalPlots = plot.rows * plot.columns;
    const currentRates = properties?.seeds?.rates_and_dosages;
    const fertiliserRates = properties?.fertilisers?.rates_and_dosages;
    const hasValidFertilisers =
      totalPlots > 0 &&
      Array.isArray(fertiliserRates) &&
      fertiliserRates.length === totalPlots;
    const applications = properties?.biologicals?.treatments;
    const hasValidBiologicals =
      totalPlots > 0 &&
      Array.isArray(applications) &&
      applications.every(
        (cells: any) =>
          Array.isArray(cells) &&
          cells.length <= totalPlots &&
          cells.every((cell: any, index: number) => {
            const cellId = cell?.plot_cell_id ?? index;
            return (
              Number.isInteger(cellId) &&
              cellId >= 0 &&
              cellId < totalPlots &&
              (cells.length === totalPlots || cell?.plot_cell_id != null)
            );
          })
      );

    if (totalPlots === currentRates?.length) {
      return {
        ...entry,
        properties: {
          ...(properties ?? {}),
          ...(applications != null && !hasValidBiologicals
            ? { biologicals: null }
            : {}),
        },
      };
    }
    return {
      ...entry,
      properties: {
        ...(hasValidFertilisers ? { fertilisers: properties.fertilisers } : {}),
        ...(hasValidBiologicals ? { biologicals: properties.biologicals } : {}),
      },
    };
  });
}

/**
 * Builds the Plot Design (step 1) PATCH payload from the current
 * `newTrialState`. Shared by the wizard's explicit Save/Next
 * (`submitPlotDesignStep`, which passes `completedStep`) and the
 * persist-on-delete flow (which omits `completedStep` so a delete never
 * advances/marks the step complete). Draft saves also omit `completedStep`.
 */
export const buildPlotDesignApiPayload = (
  newTrialState: NewTrialStateInterface,
  trialInfo: any,
  t: any,
  completedStep?: number
): any => {
  const updatedData = removeKeys(newTrialState);
  const processedTrialPlots = processTrialPlots(updatedData.trial_plots);

  let updateApiData: any = {
    ...updatedData,
    standard_field_properties: trialInfo?.standard_field_properties ?? {},
    trial_plots: processedTrialPlots,
    buffer_zone: updatedData.buffer_zone?.length
      ? updatedData.buffer_zone[0]
      : null,
    machinery_grid: updatedData.machinery_grid?.length
      ? updatedData.machinery_grid[0]
      : null,
    ab_line: cleanAbLines(updatedData.ab_line),
  };

  if (completedStep !== undefined) {
    updateApiData.completed_step = completedStep;
  }

  updateApiData = mergeStripsDataIntoPayload(
    updateApiData,
    newTrialState.stripsData,
    t,
    "common.error.console.stripDataOptimization"
  );
  if (newTrialState.stripsData?.isStripMode && !updateApiData.strip_design) {
    throw new Error(
      "Failed to serialize the strip design. The trial was not saved."
    );
  }

  // Backend contract: each strip is its own MultiPolygon plot. The in-app
  // model keeps strips as one group; split into N plots only on the wire.
  updateApiData.trial_plots = splitStripGroupsIntoPlots(
    updateApiData.trial_plots
  );

  return updateApiData;
};
